/**
 * ReviewShell — pending list page (P9c).
 *
 * Routed at /reviews. Lists pending HITL gates the backend returns to
 * the current user (filtering happens server-side via the `hitl:read`
 * permission + reviewer assignment). Clicking a row navigates to
 * `/reviews/<gate_id>` for the detail / approve / reject flow.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hitl, type GateResponse } from "../../../apiv1";

interface Props {
  onBack?: () => void;
}

export function ReviewsList({ onBack }: Props) {
  const navigate = useNavigate();
  const [gates, setGates] = useState<GateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await hitl.listGates(
        filter === "pending"
          ? { status: "pending", limit: 100 }
          : { limit: 100 },
      );
      setGates(resp.gates);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function formatRelative(iso: string): string {
    const dt = new Date(iso);
    const ms = Date.now() - dt.getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Reviews</h1>
          <p className="text-sm text-white/50 mt-1">
            HITL approval gates awaiting your decision
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("pending")}
            className={`px-3 py-1.5 text-xs rounded ${filter === "pending" ? "bg-brand-500 text-white" : "bg-white/10 hover:bg-white/15"}`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs rounded ${filter === "all" ? "bg-brand-500 text-white" : "bg-white/10 hover:bg-white/15"}`}
          >
            All
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/15 rounded ml-2"
            >
              Back
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : gates.length === 0 ? (
        <div className="text-white/40 text-sm">
          No {filter === "pending" ? "pending " : ""}gates. Your queue is clear.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="py-2 pr-4">Agent</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Confidence</th>
              <th className="py-2 pr-4">Rows</th>
              <th className="py-2 pr-4">Created</th>
              <th className="py-2 pr-4">Timeout</th>
              <th className="py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {gates.map((g) => {
              const stageName =
                (g.reasoning_chain?.stage_name as string | undefined) ?? "—";
              return (
                <tr
                  key={g.id}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate(`/reviews/${g.id}`)}
                  data-testid={`gate-row-${g.id}`}
                >
                  <td className="py-2 pr-4">
                    <div className="font-mono text-xs">{g.agent_id}</div>
                    <div className="text-[11px] text-white/40">{stageName}</div>
                  </td>
                  <td className="py-2 pr-4">
                    {g.status === "pending" ? (
                      <span className="text-amber-300">pending</span>
                    ) : g.status === "approved" ? (
                      <span className="text-emerald-400">approved</span>
                    ) : g.status === "rejected" ? (
                      <span className="text-red-400">rejected</span>
                    ) : (
                      <span className="text-orange-400">escalated</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {g.confidence_score.toFixed(2)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {g.affected_row_count.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-xs text-white/60">
                    {formatRelative(g.created_at)}
                  </td>
                  <td className="py-2 pr-4 text-xs text-white/60">
                    {g.timeout_hours}h
                    {g.escalation_level > 0 && (
                      <span className="ml-1 text-orange-400">
                        L{g.escalation_level}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right text-xs text-brand-400">
                    Review →
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
